import { useMemo, useState } from "react";
import type { AttemptEvent, ChildProfile, ContentNode, Surface } from "./types";
import { ageBandPlan, childrenSeed, contentNodes, trustEvents } from "./data/learningData";
import { applyAttempt, createAttempt, recommendContent } from "./lib/progressionEngine";
import { Header } from "./components/Header";
import { HeroSection } from "./components/HeroSection";
import { ProductConsole } from "./components/ProductConsole";
import { MarketingSections } from "./components/MarketingSections";
import { Footer } from "./components/Footer";
import { AuthModal } from "./components/AuthModal";

function App() {
  const [children, setChildren] = useState<ChildProfile[]>(childrenSeed);
  const [nodes, setNodes] = useState<ContentNode[]>(contentNodes);
  const [activeSurface, setActiveSurface] = useState<Surface>("parent");
  const [selectedChildId, setSelectedChildId] = useState(childrenSeed[0].id);
  const [attempts, setAttempts] = useState<AttemptEvent[]>([]);
  const [betaJoined, setBetaJoined] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [signedInParent, setSignedInParent] = useState(() => window.localStorage.getItem("nesty-learning-parent-email"));

  const selectedChild = useMemo(
    () => children.find((child) => child.id === selectedChildId) ?? children[0],
    [children, selectedChildId]
  );

  const recommendedNode = useMemo(
    () => recommendContent(selectedChild, nodes),
    [selectedChild, nodes]
  );

  function handleAttempt(correct: boolean, hints: number) {
    const attempt = createAttempt(selectedChild.id, recommendedNode.id, correct, hints);
    setAttempts((current) => [attempt, ...current]);
    setChildren((current) =>
      current.map((child) => {
        if (child.id !== selectedChild.id) return child;
        return {
          ...child,
          weeklyMinutes: child.weeklyMinutes + 3,
          focusScore: Math.min(98, child.focusScore + (correct ? 2 : -1)),
          frustrationSignals: correct ? child.frustrationSignals : child.frustrationSignals + 1,
          skills: child.skills.map((skill) =>
            skill.id === recommendedNode.skill ? applyAttempt(skill, correct) : skill
          )
        };
      })
    );
  }

  function handleNodeStatus(nodeId: string, status: ContentNode["status"]) {
    setNodes((current) => current.map((node) => (node.id === nodeId ? { ...node, status } : node)));
  }

  function handleBetaJoin(email: string) {
    window.localStorage.setItem("nesty-learning-beta-email", email);
    setBetaJoined(true);
  }

  function handleSignIn(email: string) {
    window.localStorage.setItem("nesty-learning-parent-email", email);
    setSignedInParent(email);
    setAuthOpen(false);
  }

  function handleSignOut() {
    window.localStorage.removeItem("nesty-learning-parent-email");
    setSignedInParent(null);
  }

  return (
    <div className="app-shell">
      <Header
        parentEmail={signedInParent}
        onJoinClick={() => document.getElementById("join-beta")?.scrollIntoView({ behavior: "smooth" })}
        onSignInClick={() => setAuthOpen(true)}
        onSignOut={handleSignOut}
      />
      <main>
        <HeroSection betaJoined={betaJoined} onBetaJoin={handleBetaJoin} />
        <ProductConsole
          activeSurface={activeSurface}
          attempts={attempts}
          children={children}
          nodes={nodes}
          recommendedNode={recommendedNode}
          selectedChild={selectedChild}
          selectedChildId={selectedChildId}
          setActiveSurface={setActiveSurface}
          setSelectedChildId={setSelectedChildId}
          trustEvents={trustEvents}
          onAttempt={handleAttempt}
          onNodeStatus={handleNodeStatus}
        />
        <MarketingSections ageBandPlan={ageBandPlan} />
      </main>
      <Footer />
      <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} onSignIn={handleSignIn} />
    </div>
  );
}

export default App;
